import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { FormInstance } from "antd/es/form/Form";
import { useIntl } from "react-intl";

import { getActivityTypes } from "@app/store/activity";

import { getAssociationsState } from "../store";
import FormValues from "../types/FormValues";

const useActivityNameAutoFill = (
  form: FormInstance<FormValues>,
  isActivityNameChangedByUser: boolean,
  isFollowUpActivity: boolean
) => {
  const intl = useIntl();

  const activityTypes = useSelector(getActivityTypes);
  const associations = useSelector(getAssociationsState);

  const nameFieldName: keyof FormValues = "name";
  const activityTypeIdFieldName: keyof FormValues = "activityTypeId";

  const autoFillActivityName = useCallback(() => {
    if (isActivityNameChangedByUser) {
      return;
    }

    const activityTypeId = form.getFieldValue(activityTypeIdFieldName);

    const activityTypeName = activityTypes.find(
      (activityType) => activityType.id === activityTypeId
    )?.name;

    const recordName =
      associations.associatedCompany?.name ||
      associations.associatedPerson?.name ||
      associations.associatedDeal?.name;

    let activityName: string;
    if (activityTypeName) {
      activityName = intl.formatMessage(
        {
          id: "createActivityModal.autoFill.name",
          defaultMessage:
            "{isFollowUpActivity, select, true {Follow-up } other {}}{activityTypeName} {recordName, select, undefined {} other {with {recordName}}}",
          description: "Default activity name",
        },
        {
          activityTypeName,
          isFollowUpActivity,
          recordName,
        }
      );
    } else {
      activityName = "";
    }

    form.setFieldValue(nameFieldName, activityName);
  }, [activityTypes, associations, form, intl, isFollowUpActivity, isActivityNameChangedByUser]);

  useEffect(() => {
    autoFillActivityName();
  }, [activityTypes, associations, autoFillActivityName, form, intl]);

  const handleValuesChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (changedValues: Partial<FormValues>, _: FormValues) => {
      if (!isActivityNameChangedByUser && changedValues.activityTypeId) {
        autoFillActivityName();
      }
    },
    [autoFillActivityName, isActivityNameChangedByUser]
  );

  return [handleValuesChange];
};

export default useActivityNameAutoFill;
